import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Agreement } from '../../backend/types/agreements.ts'
import { UserInfo } from '../../backend/types/userInfo.ts'
import { mutationHandler } from '../mutationHandler.ts'

export const useGetUser = () => useQuery<UserInfo>({ queryKey: ['user'] })

export const useGetTermsOfUse = (userInfo?: UserInfo) => useQuery<Agreement>({
    queryKey: ['user/terms_of_use'],
    enabled: !!userInfo && !userInfo.termsOfUseAccepted
})

const logout = () => fetch(`/api/user/logout`, {
    method: 'POST',
    headers: {
        "Content-Type": "application/json",
    }
})

export const useLogoutMutation = () => useMutation({
    mutationFn: mutationHandler<null, typeof logout>(logout)
})


const acceptTermsOfUse = (data: Pick<Agreement, 'body' | 'id'>) => fetch(`/api/user/terms_of_use`, {
    method: 'POST',
    headers: {
        "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
})

export const useAcceptTermsOfUse = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: mutationHandler<Pick<Agreement, 'body' | 'id'>, typeof acceptTermsOfUse>(acceptTermsOfUse),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`user`] })
            queryClient.invalidateQueries({ queryKey: ['case'] })
        },
    })
}

const updateUser = (data: Partial<UserInfo>) => fetch(`/api/user`, {
    method: 'PUT',
    headers: {
        "Content-Type": "application/json",
    },
    body: JSON.stringify(data) }
)

const updateUserLayout = (data: UserInfo["layoutConfig"]) => updateUser({ layoutConfig: data })

export const useUpdateUserLayoutMutation = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: mutationHandler<null, typeof updateUserLayout>(updateUserLayout),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`user`] })
        },
    })
}

const updateUserDarkMode = (enabled: boolean) => updateUser({ appConfig: { darkMode: enabled } })

export const useUpdateUserDarkModeMutation = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: mutationHandler<null, typeof updateUserDarkMode>(updateUserDarkMode),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`user`] })
        },
    })
}
